import React, { useState, useRef, useEffect, useMemo } from "react";
import { apiManager } from "../../services/apiManager";
import { outpatientAPI, clinicAppointmentsAPI, inpatientAPI, handleApiError } from "../../services/api";
import AppointmentModal from "../modals/AppointmentModal";
import EditAppointmentModal from "../modals/EditAppointmentModal";
import SelectPaymentModal from "../modals/SelectPaymentModal"; // Commented out for now - will be used later
import { showErrorToast, showSuccessToast } from "../../utils/notificationUtils";
import { USER_TYPES } from "../../constants/userTypes";
import { useTranslation } from "react-i18next";
import "../../pages/reception/ReceptionStyles.css";
import { useFlatpickrUserType } from "../../hooks/useFlatpickrUserType";
import { isClinicOnFreePlan } from "../../utils/clinicPlanUtils";

const ROWS_PER_PAGE = 8;

const AppointmentsTab = ({
  clinicData,
  clinicDataFromHook,
  userType,
  vets,
  loadingLists,
  appointments = [],
  fetchAllTabData,
  onDataUpdate
}) => {
  // Apply user type color to Flatpickr calendars globally
  useFlatpickrUserType();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.language === 'ar';
  const locale = i18n.language === "ar" ? "ar-EG" : "en-US";
  const fallbackLabels = useMemo(
    () => ({
      pet: t("receptionAppointments.fallback.pet"),
      owner: t("receptionAppointments.fallback.owner"),
      vet: t("receptionAppointments.fallback.vet"),
      reason: t("receptionAppointments.fallback.reason"),
      date: t("receptionAppointments.fallback.date"),
      time: t("receptionAppointments.fallback.time"),
      searchResults: t("receptionAppointments.header.searchTag"),
    }),
    [t]
  );
  const formatStatus = (status) =>
    t(`receptionAppointments.status.${status}`, {
      defaultValue: status,
    });

  // Helper function to determine appointment status based on date and attendance
  const determineAppointmentStatus = (appointment) => {
    let status = appointment.status || 'pending';

    // Check if appointment date has passed
    if (appointment.appointment_at) {
      const appointmentDateTime = new Date(appointment.appointment_at);
      const now = new Date();

      // If appointment date/time has passed
      if (appointmentDateTime < now) {
        // Check if patient attended (has associated visit)
        const hasOutpatientVisit = appointment.outpatient_visit_id !== null &&
          appointment.outpatient_visit_id !== undefined &&
          appointment.outpatient_visit_id !== 0;
        const hasInpatientVisit = appointment.inpatient_visit_id !== null &&
          appointment.inpatient_visit_id !== undefined &&
          appointment.inpatient_visit_id !== 0;
        const hasVisit = hasOutpatientVisit || hasInpatientVisit;

        // If patient attended, status is "closed", otherwise "no_show"
        if (hasVisit) {
          status = 'closed';
        } else {
          status = 'no_show';
        }
      }
    }

    return status;
  };
  const formatAppointmentReason = (date, time, vet) =>
    t("receptionAppointments.row.defaultReason", {
      date,
      time,
      vet,
    });
  const [localAppointments, setLocalAppointments] = useState(appointments);
  const [selectedRow, setSelectedRow] = useState(null);
  const [showNewModal, setShowNewModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editAppointmentData, setEditAppointmentData] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [appointmentToCancel, setAppointmentToCancel] = useState(null);
  const [showAdmitModal, setShowAdmitModal] = useState(false);
  const [showAdmitInpatientModal, setShowAdmitInpatientModal] = useState(false);
  const [admitDownpayment, setAdmitDownpayment] = useState('');
  const [admitPaymentType, setAdmitPaymentType] = useState("2");
  const [paymentTypes, setPaymentTypes] = useState([]);
  const [loadingPaymentTypes, setLoadingPaymentTypes] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  const [isAdmitting, setIsAdmitting] = useState(false);
  // Commented out for now - will be used later
  // const [showOutpatientPaymentModal, setShowOutpatientPaymentModal] = useState(false);
  // const [outpatientPaymentNotes, setOutpatientPaymentNotes] = useState("");
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState({
    // Basic search fields
    date: new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0],
    pet_owner: "",
    pet: "",
    vet: "",

    // Advanced search parameters
    user_id: "",
    vet_id: "",
    clinic_id: "",
    pet_id: "",
    pet_owner_id: "",
    appointment_type_id: "",
    status: "",
    date_from: "",
    date_to: "",
    datetime_from: "",
    datetime_to: "",
    time_from: "",
    time_to: "",
    search: "",
    user_name: "",
    vet_name: "",
    pet_name: "",
    clinic_name: "",
    canceled_by: "",
    restored_by: "",
    has_canceled_reason: false,
    today: false,
    tomorrow: false,
    this_week: false,
    this_month: false,
    upcoming: false,
    past: false,
    sort_by: "appointment_at",
    sort_order: "asc",
    per_page: 5
  });

  const [viewType, setViewType] = useState('table'); // 'table' or 'calendar'

  const handleQuickFilter = (type, forcedViewType = null) => {
    // Use local time for today and tomorrow to handle timezones correctly
    const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0];
    const tomorrowDate = new Date();
    tomorrowDate.setDate(tomorrowDate.getDate() + 1);
    const tomorrow = new Date(tomorrowDate.getTime() - tomorrowDate.getTimezoneOffset() * 60000).toISOString().split('T')[0];

    setSearch(s => {
      const newSearch = {
        ...s,
        date: type === 'today' ? today : type === 'tomorrow' ? tomorrow : "",
        upcoming: type === 'upcoming',
        today: type === 'today',
        tomorrow: type === 'tomorrow',
        sort_order: type === 'past' ? 'desc' : 'asc'
      };

      // Trigger search immediately
      const searchParams = {
        ...newSearch,
        date_from: newSearch.date || "",
        date_to: newSearch.date || "",
        per_page: (forcedViewType || viewType) === 'calendar' ? 20 : 5
      };
      searchAppointments(searchParams);

      return newSearch;
    });
    setPage(1);
  };

  // Tooltip menu state
  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
  const [tooltipRow, setTooltipRow] = useState(null);

  // Help tooltip state
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);
  const [helpTooltipPosition, setHelpTooltipPosition] = useState({ x: 0, y: 0 });

  // Search state
  const [isSearching, setIsSearching] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [totalSearchResults, setTotalSearchResults] = useState(0);
  const [searchPagination, setSearchPagination] = useState(null);

  // Refs for Flatpickr
  const searchDateInputRef = useRef(null);
  const searchDatePickerRef = useRef(null);

  // Update local state when props change
  useEffect(() => {
    setLocalAppointments(appointments);
  }, [appointments]);

  // Effect to handle navigation from notifications where the appointment might be on a different day
  useEffect(() => {
    const handleNotificationNavigation = async () => {
      const highlightId = sessionStorage.getItem('highlightAppointmentId');
      if (!highlightId) return;

      // 1. Check if already in the list to avoid unnecessary API calls
      // Note: localAppointments might be updated asynchronously, so we check if we found it
      const foundInList = localAppointments.some(a => a.id.toString() === highlightId.toString());
      if (foundInList) {
        return;
      }

      // 2. Fetch the appointment details to find its date
      try {
        const response = await clinicAppointmentsAPI.getAppointment(highlightId);
        const appointmentData = response.data?.data || response.data;

        if (appointmentData && appointmentData.appointment_at) {
          const aptDate = appointmentData.appointment_at.split('T')[0];

          // Calculate local dates
          const localNow = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
          const todayStr = localNow.toISOString().split('T')[0];

          const tomorrowDate = new Date(localNow);
          tomorrowDate.setDate(tomorrowDate.getDate() + 1);
          const tomorrowStr = tomorrowDate.toISOString().split('T')[0];

          // 3. Determine the correct view and update search
          let newSearch = { ...search };
          let shouldSearch = false;
          let newViewType = viewType;

          if (aptDate === todayStr) {
            newSearch = { ...newSearch, date: todayStr, upcoming: false, today: true, tomorrow: false, past: false };
            shouldSearch = true;
          } else if (aptDate === tomorrowStr) {
            newSearch = { ...newSearch, date: tomorrowStr, upcoming: false, today: false, tomorrow: true, past: false };
            shouldSearch = true;
          } else if (aptDate > todayStr) {
            // For upcoming, we clear date but keep upcoming flag
            newSearch = { ...newSearch, date: "", upcoming: true, today: false, tomorrow: false, past: false };
            shouldSearch = true;
          } else {
            // Past
            newSearch = { ...newSearch, date: aptDate, upcoming: false, today: false, tomorrow: false, past: true };
            shouldSearch = true;
          }

          if (shouldSearch) {
            setSearch(newSearch);

            // Check view type for pagination 
            const perPage = newViewType === 'calendar' ? 20 : 5;

            const searchParams = {
              ...newSearch,
              date_from: newSearch.date || "",
              date_to: newSearch.date || "",
              per_page: perPage
            };

            await searchAppointments(searchParams);
          }
        }
      } catch (error) {
        console.error("Failed to resolve appointment date from notification", error);
      }
    };

    // Run this effect once on mount to handle initial navigation
    handleNotificationNavigation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Check for appointment ID to highlight from sessionStorage (from notification click)
  useEffect(() => {
    const highlightAppointmentId = sessionStorage.getItem('highlightAppointmentId');
    if (highlightAppointmentId && localAppointments.length > 0) {
      // Find the appointment to highlight
      const appointmentToHighlight = localAppointments.find(
        apt => apt.id?.toString() === highlightAppointmentId.toString()
      );

      if (appointmentToHighlight) {
        // Convert appointment to row format for highlighting
        const appointmentDate = appointmentToHighlight.appointment_at
          ? new Date(appointmentToHighlight.appointment_at).toISOString().split('T')[0]
          : '';
        const appointmentTime = appointmentToHighlight.appointment_at
          ? new Date(appointmentToHighlight.appointment_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          : '';

        const rowToHighlight = {
          id: appointmentToHighlight.id,
          date: appointmentDate,
          time: appointmentTime,
          pet: appointmentToHighlight.pet?.name || fallbackLabels.pet,
          pet_owner:
            appointmentToHighlight.petOwner?.full_name || fallbackLabels.owner,
          vet:
            appointmentToHighlight.vet?.user?.name ||
            appointmentToHighlight.vet?.name ||
            fallbackLabels.vet,
          reason: appointmentToHighlight.reason || '',
          status: appointmentToHighlight.status || 'pending',
          originalData: appointmentToHighlight
        };

        // Set selected row to highlight it
        setSelectedRow(rowToHighlight);

        // Scroll to the highlighted row after a short delay
        setTimeout(() => {
          const rowElement = document.querySelector(`[data-appointment-id="${appointmentToHighlight.id}"]`);
          if (rowElement) {
            rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Add a temporary highlight animation
            rowElement.style.transition = 'background-color 0.3s ease';
            rowElement.style.backgroundColor = '#fef3c7';
            setTimeout(() => {
              rowElement.style.backgroundColor = '';
            }, 2000);
          }
        }, 500);

        // Clear the sessionStorage after highlighting
        sessionStorage.removeItem('highlightAppointmentId');
      }
    }
  }, [localAppointments]);

  // Fetch payment types when component mounts
  useEffect(() => {
    fetchPaymentTypes();
  }, []);

  // Fetch payment types when admit modal opens
  useEffect(() => {
    if (showAdmitModal && paymentTypes.length === 0) {
      fetchPaymentTypes();
    }
  }, [showAdmitModal]);

  // Use environment variable for backend API base URL
  const API_BASE_URL = import.meta.env.VITE_API_URL || "http://localhost:8000";

  // Fetch payment types from API
  const fetchPaymentTypes = async () => {
    try {
      setLoadingPaymentTypes(true);
      const token = localStorage.getItem("access_token");

      const response = await fetch(`${API_BASE_URL}/api/v1/payment-types`, {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();

      if (data.success && data.data) {
        // Transform the API response to match the expected format
        const transformedPaymentTypes = data.data.map(payment => ({
          value: payment.id.toString(), // Use ID as value
          label: payment.name, // Use name as label
          type: payment.type, // Keep original type for reference
          type_display: payment.type_display // Keep display type for reference
        }));

        setPaymentTypes(transformedPaymentTypes);

        // Set default payment type to first available type or try to find credit card
        const defaultType = transformedPaymentTypes.find(type =>
          type.label.toLowerCase().includes('credit') ||
          type.label.toLowerCase().includes('card')
        ) || transformedPaymentTypes[0];

        if (defaultType) {
          setAdmitPaymentType(defaultType.value);
        }
      }
    } catch (error) {
      console.error("Error fetching payment types:", error);
      // Fallback to default payment types if API fails
      setPaymentTypes([
        { value: 'credit_card', label: '💳 Credit Card' },
        { value: 'cash', label: '💵 Cash' },
        { value: 'debit_card', label: '💳 Debit Card' },
        { value: 'bank_transfer', label: '🏦 Bank Transfer' },
        { value: 'mobile_payment', label: '📱 Mobile Payment' },
        { value: 'check', label: '📄 Check' }
      ]);
    } finally {
      setLoadingPaymentTypes(false);
    }
  };

  // Get clinic data based on user type
  const clinicDataToUse = userType === USER_TYPES.CLINIC_MODERATOR
    ? (clinicDataFromHook || clinicData)
    : clinicData;

  // Check if clinic is on free plan
  const isFreePlan = isClinicOnFreePlan(clinicDataToUse);

  // Initialize Flatpickr for search date input
  useEffect(() => {
    if (searchDateInputRef.current && !searchDatePickerRef.current) {
      import("flatpickr").then((flatpickr) => {
        searchDatePickerRef.current = flatpickr.default(searchDateInputRef.current, {
          // Keep internal format compatible with API and filtering
          dateFormat: "Y-m-d",
          // Show users dd-mm-YYYY in the visible input
          altInput: true,
          altFormat: "d-m-Y",
          // Ensure alternate input matches existing styling
          altInputClass: "reception-input",
          allowInput: true,
          clickOpens: true,
          defaultDate: "today",
          onChange: function (selectedDates, dateStr) {
            // Store canonical YYYY-MM-DD while displaying dd-mm-YYYY
            setSearch((s) => ({ ...s, date: dateStr }));
            setPage(1);
          },
          onReady: function (selectedDates, dateStr, instance) {
            // Set initial search state to today's date (local time)
            const today = new Date(Date.now() - new Date().getTimezoneOffset() * 60000).toISOString().split('T')[0];
            setSearch((s) => ({ ...s, date: today }));
          }
        });
      });
    }
    return () => {
      if (searchDatePickerRef.current) {
        searchDatePickerRef.current.destroy();
        searchDatePickerRef.current = null;
      }
    };
  }, []);

  // Sync Flatpickr with search.date state changes
  useEffect(() => {
    if (searchDatePickerRef.current) {
      if (search.date) {
        searchDatePickerRef.current.setDate(search.date, false);
      } else {
        searchDatePickerRef.current.clear(false);
      }
    }
  }, [search.date]);

  // Function to update existing appointment
  const updateAppointmentToAPI = async (appointment) => {
    const token = localStorage.getItem("access_token");
    const originalAppointment = appointments.find(apt => apt.id === appointment.id);

    try {
      setIsUpdating(true);
      if (loadingLists) {
        showErrorToast(t("receptionAppointments.toasts.loading"));
        return false;
      }


      // Validate required data
      if (!appointment.pet_id) {
        showErrorToast(t("receptionAppointments.toasts.petRequired"));
        return false;
      }
      if (!appointment.vet_id) {
        showErrorToast(t("receptionAppointments.toasts.vetRequired"));
        return false;
      }
      if (!clinicDataToUse?.clinic) {
        showErrorToast(t("receptionAppointments.toasts.clinicMissing"));
        return false;
      }

      // Combine date and time
      const appointment_at =
        appointment.date && appointment.time
          ? `${appointment.date} ${appointment.time}:00`
          : null;

      if (!appointment_at) {
        showErrorToast(t("receptionAppointments.toasts.datetimeRequired"));
        return false;
      }

      const payload = {
        pet_id: appointment.pet_id,
        clinic_id: clinicDataToUse.clinic.id,
        vet_id: appointment.vet_id,
        appointment_at: appointment_at,
        pet_owner_id: appointment.pet_owner_id,
        reason: appointment.reason,
        appointment_type_id: appointment.appointment_type_id,
      };


      // Update local state immediately for better UX
      const updatedAppointment = {
        ...appointment,
        // Keep the display data updated
        date: appointment.date,
        time: appointment.time,
        pet_owner: appointment.pet_owner
          ? `${appointment.pet_owner.first_name || ''} ${appointment.pet_owner.last_name || ''}`.trim()
          : appointment.pet_owner_id
            ? fallbackLabels.owner
            : '',
        pet: appointment.pet?.name || (appointment.pet_id ? fallbackLabels.pet : ''),
        vet: appointment.vet ?
          (appointment.vet.user?.name || appointment.vet.name || `Vet ${appointment.vet_id}`) :
          (appointment.vet_id ? `Vet ${appointment.vet_id}` : fallbackLabels.vet),
        reason: appointment.reason,
        originalData: {
          ...originalAppointment?.originalData,
          ...appointment,
          appointment_at: appointment_at,
        }
      };

      // Update local appointments state immediately
      const updatedAppointments = localAppointments.map(apt =>
        apt.id === appointment.id ? updatedAppointment : apt
      );
      updateAppointments(updatedAppointments);

      // Send to backend
      const res = await fetch(`${API_BASE_URL}/api/v1/clinic-appointments/${appointment.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json();
        console.error("API Error:", errorData);

        // Revert the local changes on API error
        const revertedAppointments = localAppointments.map(apt =>
          apt.id === appointment.id ? originalAppointment : apt
        );
        updateAppointments(revertedAppointments);

        throw new Error(errorData.message || "Failed to update appointment");
      }

      const responseData = await res.json();

      showSuccessToast(t("receptionAppointments.toasts.updateSuccess"));
      setShowEditModal(false);
      setEditAppointmentData(null);

      // Refresh appointments list to ensure data consistency and correct sorting/filtering
      // This is important if the date changed or status changed, affecting the list view
      await searchAppointments(search);

      return true; // Success

    } catch (err) {
      console.error("Error updating appointment:", err);
      showErrorToast(
        err.message || t("receptionAppointments.toasts.updateFailed")
      );
      return false; // Failure
    } finally {
      setIsUpdating(false);
    }
  };

  // Function to save new appointment to API
  const saveAppointmentToAPI = async (appointment) => {
    const token = localStorage.getItem("access_token");
    try {
      setIsSaving(true);
      if (loadingLists) {
        showErrorToast(t("receptionAppointments.toasts.loading"));
        return false;
      }


      // Validate required data
      if (!appointment.pet_id) {
        showErrorToast(t("receptionAppointments.toasts.petRequired"));
        return false;
      }
      if (!appointment.vet_id) {
        showErrorToast(t("receptionAppointments.toasts.vetRequired"));
        return false;
      }
      if (!clinicDataToUse?.clinic) {
        showErrorToast(t("receptionAppointments.toasts.clinicMissing"));
        return false;
      }
      if (!appointment.appointment_type_id) {
        showErrorToast(t("receptionAppointments.toasts.typeRequired"));
        return false;
      }
      // Combine date and time
      const appointment_at =
        appointment.date && appointment.time
          ? `${appointment.date} ${appointment.time}:00`
          : null;

      if (!appointment_at) {
        showErrorToast(t("receptionAppointments.toasts.datetimeRequired"));
        return false;
      }

      const payload = {
        pet_id: appointment.pet_id,
        clinic_id: clinicDataToUse.clinic.id,
        vet_id: appointment.vet_id,
        appointment_at: appointment_at,
        pet_owner_id: appointment.pet_owner_id,
        reason: appointment.reason,
        appointment_type_id: appointment.appointment_type_id,
      };


      // Send to backend
      const res = await fetch(`${API_BASE_URL}/api/v1/clinic-appointments`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
          accept: "application/json",
        },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const errorData = await res.json();
        console.error("API Error:", errorData);
        throw new Error(errorData.message || "Failed to save appointment");
      }

      const responseData = await res.json();

      showSuccessToast(t("receptionAppointments.toasts.saveSuccess"));

      // Add new appointment to local state for immediate UI feedback
      const newAppointment = {
        ...appointment,
        id: responseData.data?.id || Date.now(),
        date: appointment.date,
        time: appointment.time,
        pet_owner: appointment.pet_owner
          ? `${appointment.pet_owner.first_name || ''} ${appointment.pet_owner.last_name || ''}`.trim()
          : fallbackLabels.owner,
        pet: appointment.pet?.name || fallbackLabels.pet,
        vet: appointment.vet ?
          (appointment.vet.user?.name || appointment.vet.name || `Vet ${appointment.vet_id}`) :
          `Vet ${appointment.vet_id}`,
        reason: appointment.reason,
        status: "pending",
        type: "appointment",
        originalData: {
          ...appointment,
          appointment_at: appointment_at,
        }
      };

      updateAppointments([...localAppointments, newAppointment]);

      // Refresh all tab data from server to ensure consistency
      if (fetchAllTabData) {
        await fetchAllTabData(true); // Force refresh to get latest data
      }

      return true; // Success

    } catch (err) {
      console.error("Error saving appointment:", err);
      showErrorToast(
        err.message || t("receptionAppointments.toasts.saveFailed")
      );
      return false; // Failure
    } finally {
      setIsSaving(false);
    }
  };

  // Update parent data when local state changes
  const updateAppointments = (newAppointments) => {
    setLocalAppointments(newAppointments);
    if (onDataUpdate) {
      onDataUpdate(newAppointments);
    }
  };

  // Search appointments using API
  const searchAppointments = async (searchParams = {}) => {
    try {
      setIsSearching(true);

      // Build search parameters with only non-empty values
      const params = {
        page: page,
        per_page: searchParams.per_page || (viewType === 'calendar' ? 20 : 5),
        clinic_id: clinicDataToUse?.clinic?.id,
      };

      // Only add parameters that have actual values
      if (searchParams.date_from) params.date_from = searchParams.date_from;
      if (searchParams.date_to) params.date_to = searchParams.date_to;
      if (searchParams.datetime_from) params.datetime_from = searchParams.datetime_from;
      if (searchParams.datetime_to) params.datetime_to = searchParams.datetime_to;
      if (searchParams.time_from) params.time_from = searchParams.time_from;
      if (searchParams.time_to) params.time_to = searchParams.time_to;
      if (searchParams.user_id) params.user_id = searchParams.user_id;
      if (searchParams.vet_id) params.vet_id = searchParams.vet_id;
      if (searchParams.pet_id) params.pet_id = searchParams.pet_id;
      if (searchParams.pet_owner_id) params.pet_owner_id = searchParams.pet_owner_id;
      if (searchParams.appointment_type_id) params.appointment_type_id = searchParams.appointment_type_id;

      // Handle special 'upcoming' status which corresponds to a backend flag
      if (searchParams.status === 'upcoming') {
        // Manually filter for future active appointments instead of relying on the backend 'upcoming' flag
        // which might return past pending appointments (No Shows)
        const localNow = new Date(Date.now() - new Date().getTimezoneOffset() * 60000);
        params.date_from = localNow.toISOString().split('T')[0];
        params.status = 'pending,confirmed,arrived,in_progress';
      } else if (searchParams.status && searchParams.status !== '') {
        params.status = String(searchParams.status);
      }

      if (searchParams.user_name) params.pet_owner_name = searchParams.user_name;
      if (searchParams.vet_name) params.vet_name = searchParams.vet_name;
      if (searchParams.pet_name) params.pet_name = searchParams.pet_name;
      if (searchParams.clinic_name) params.clinic_name = searchParams.clinic_name;
      if (searchParams.search) params.search = searchParams.search;
      if (searchParams.canceled_by) params.canceled_by = searchParams.canceled_by;
      if (searchParams.restored_by) params.restored_by = searchParams.restored_by;
      if (searchParams.has_canceled_reason) params.has_canceled_reason = searchParams.has_canceled_reason;
      if (searchParams.today) params.today = searchParams.today;
      if (searchParams.tomorrow) params.tomorrow = searchParams.tomorrow;
      if (searchParams.this_week) params.this_week = searchParams.this_week;
      if (searchParams.this_month) params.this_month = searchParams.this_month;
      if (searchParams.upcoming) params.upcoming = searchParams.upcoming;
      if (searchParams.past) params.past = searchParams.past;
      if (searchParams.sort_by) params.sort_by = searchParams.sort_by;
      if (searchParams.sort_order) params.sort_order = searchParams.sort_order;



      const response = await clinicAppointmentsAPI.searchAppointments(params);

      if (response.data && response.data.status === "success") {
        const appointments = response.data.data.appointments || [];
        const pagination = response.data.data.pagination || {};
        const total = pagination.total || appointments.length;

        // Transform appointments to match the expected format
        let transformedAppointments = appointments.map(appointment => {
          // Determine status based on date and attendance
          const status = determineAppointmentStatus(appointment);
          const relatedStatus = searchParams.status === 'upcoming' ? 'upcoming' : status;

          return {
            id: appointment.id,
            date: appointment.appointment_at ? appointment.appointment_at.split('T')[0].split(' ')[0] : '',
            time: appointment.appointment_at || '', // Preserve full string for Date parsing in render
            pet_owner: appointment.pet_owner
              ? `${appointment.pet_owner.first_name || ''} ${appointment.pet_owner.last_name || ''}`.trim()
              : fallbackLabels.owner,
            pet: appointment.pet?.name || fallbackLabels.pet,
            vet: appointment.vet
              ? `${appointment.vet.first_name || ''} ${appointment.vet.last_name || ''}`.trim()
              : fallbackLabels.vet,
            reason: appointment.reason || '',
            status: status,
            type: 'appointment',
            originalData: appointment
          };
        });

        // Filter out past appointments if searching for 'upcoming'
        // This is a safety measure because backend date filtering might be inconsistent
        if (searchParams.status === 'upcoming') {
          const localNowDate = new Date().toISOString().split('T')[0];
          transformedAppointments = transformedAppointments.filter(apt => {
            // Keep only if date is today or future, AND status is not a past-tense status
            // Note: 'no_show' is calculated for past pending appointments
            const isPastDate = apt.date < localNowDate;
            if (isPastDate) return false;

            return !['no_show', 'completed', 'checkout', 'closed', 'canceled'].includes(apt.status);
          });
        }

        // Sort results by date/time (Earliest to Furthest)
        transformedAppointments.sort((a, b) => {
          const dateA = new Date(a.time);
          const dateB = new Date(b.time);
          return dateA - dateB;
        });

        setSearchResults(transformedAppointments);
        setTotalSearchResults(transformedAppointments.length); // Update total count to match filtered results
        setSearchPagination(pagination);

        // Update local appointments for immediate UI feedback
        updateAppointments(transformedAppointments);

        return transformedAppointments;
      }

      return [];
    } catch (error) {
      console.error("Error searching appointments:", error);
      showErrorToast(
        handleApiError ? handleApiError(error) : t("receptionAppointments.toasts.searchFailed")
      );
      return [];
    } finally {
      setIsSearching(false);
    }
  };

  // Use search results if available, otherwise use local filtering
  const filteredRows = searchResults.length > 0 ? searchResults : localAppointments.filter((row) => {
    const passesDateFilter = !search.date || row.date.includes(search.date);
    const passesPetOwnerFilter = !search.pet_owner ||
      row.pet_owner.toLowerCase().includes(search.pet_owner.toLowerCase());
    const passesPetFilter = !search.pet ||
      row.pet.toLowerCase().includes(search.pet.toLowerCase());
    const passesVetFilter = !search.vet ||
      row.vet.toLowerCase().includes(search.vet.toLowerCase());
    const passesStatusFilter = !search.status || (row.status && row.status === search.status);

    return passesDateFilter && passesPetOwnerFilter && passesPetFilter && passesVetFilter && passesStatusFilter;
  });

  // Final filtering logic: Hide inactive statuses unless explicitly selected in the filter
  const visibleFilteredRows = search.status
    ? filteredRows
    : filteredRows.filter(row => !['completed', 'no_show', 'closed'].includes(row.status));

  // Pagination - use search results if available, otherwise use local filtering
  const totalPages = searchResults.length > 0
    ? (searchPagination?.last_page || 1)
    : Math.ceil(visibleFilteredRows.length / ROWS_PER_PAGE);

  const paginatedRows = searchResults.length > 0
    ? visibleFilteredRows // Search results were already paginated by the API, but we might have filtered them further on client
    : visibleFilteredRows.slice(
      (page - 1) * ROWS_PER_PAGE,
      page * ROWS_PER_PAGE
    );

  // If we've filtered the server results on the client, the 'totalSearchResults' from server might be inaccurate.
  // We use visibleFilteredRows.length for the count to ensure it matches the visible cards in the current view.
  const totalDisplayed = (searchResults.length > 0 && search.status) ? totalSearchResults : visibleFilteredRows.length;
  const countLabel = t("receptionAppointments.header.count", { count: totalDisplayed });
  const formattedCancelDate = appointmentToCancel?.date
    ? new Date(`${appointmentToCancel.date}T00:00:00`).toLocaleDateString(locale)
    : fallbackLabels.date;
  const formattedCancelTime = appointmentToCancel?.time
    ? new Date(`1970-01-01T${appointmentToCancel.time}:00`).toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    })
    : fallbackLabels.time;
  const formattedSelectedDate = selectedRow?.date
    ? new Date(`${selectedRow.date}T00:00:00`).toLocaleDateString(locale)
    : fallbackLabels.date;
  const formattedSelectedTime = selectedRow?.time
    ? new Date(`1970-01-01T${selectedRow.time}:00`).toLocaleTimeString(locale, {
      hour: '2-digit',
      minute: '2-digit',
    })
    : fallbackLabels.time;

  // Handle row hover to show help tooltip
  const handleRowHover = (row, event) => {
    if (!showTooltip) {
      setHelpTooltipPosition({
        x: event.clientX,
        y: event.clientY
      });
      setShowHelpTooltip(true);
    }
  };

  const handleRowLeave = () => {
    setShowHelpTooltip(false);
  };

  // Handle row click to show tooltip (both left and right click)
  const handleRowClick = (row, event) => {
    event.preventDefault();
    event.stopPropagation();

    // Hide help tooltip
    setShowHelpTooltip(false);

    // Set selected row for highlighting
    setSelectedRow(row);

    setTooltipPosition({
      x: event.clientX,
      y: event.clientY
    });
    setTooltipRow(row);
    setShowTooltip(true);
  };

  // Handle right-click context menu
  const handleRowRightClick = (row, event) => {
    event.preventDefault();
    event.stopPropagation();

    // Hide help tooltip
    setShowHelpTooltip(false);

    // Set selected row for highlighting
    setSelectedRow(row);

    setTooltipPosition({
      x: event.clientX,
      y: event.clientY
    });
    setTooltipRow(row);
    setShowTooltip(true);
  };

  // Handle tooltip menu actions
  const handleTooltipAction = (action, row) => {
    setShowTooltip(false);
    setSelectedRow(row);

    switch (action) {
      case 'edit':
        // For edit mode, we need to pass the original appointment data
        setEditAppointmentData(row.originalData || row);
        setShowEditModal(true);
        break;
      case 'cancel':
        // set the appointment to cancel and show confirm modal
        setAppointmentToCancel(row);
        setShowCancelModal(true);
        break;
      case 'admit_outpatient':
        setAdmitDownpayment('');
        // Set default payment type to first available or credit card if available
        const defaultOutpatientType = paymentTypes.find(type =>
          type.label.toLowerCase().includes('credit') ||
          type.label.toLowerCase().includes('card')
        ) || paymentTypes[0];
        setAdmitPaymentType(defaultOutpatientType?.value || "2"); // Default to Credit Card ID
        setShowAdmitModal(true);
        break;
      case 'admit_inpatient':
        setAdmitDownpayment('');
        // Set default payment type to first available or credit card if available
        const defaultInpatientType = paymentTypes.find(type =>
          type.label.toLowerCase().includes('credit') ||
          type.label.toLowerCase().includes('card')
        ) || paymentTypes[0];
        setAdmitPaymentType(defaultInpatientType?.value || "2"); // Default to Credit Card ID
        setShowAdmitInpatientModal(true);
        break;
      default:
        break;
    }
  };

  // Hide tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (showTooltip && !event.target.closest('.tooltip-menu')) {
        setShowTooltip(false);
      }
      if (showHelpTooltip && !event.target.closest('tr')) {
        setShowHelpTooltip(false);
      }
    };

    if (showTooltip || showHelpTooltip) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [showTooltip, showHelpTooltip]);

  // Confirm cancellation from tooltip
  const confirmCancellation = async () => {
    if (!appointmentToCancel) return;
    try {
      // optimistic update: remove from local list
      const appointmentId = appointmentToCancel.id;
      const updated = localAppointments.filter((app) => app.id !== appointmentId);
      updateAppointments(updated);

      // call cancel API
      await clinicAppointmentsAPI.cancelAppointment(appointmentId);

      showSuccessToast(t("receptionAppointments.toasts.cancelSuccess"));
    } catch (error) {
      console.error('Failed to cancel appointment:', error);
      showErrorToast(t("receptionAppointments.toasts.cancelFailed"));
      // revert by refetching data
      fetchAllTabData(true);
    } finally {
      setAppointmentToCancel(null);
      setShowCancelModal(false);
    }
  };

  const closeCancelModal = () => {
    setAppointmentToCancel(null);
    setShowCancelModal(false);
  };

  // Helper function to perform outpatient conversion
  const performOutpatientConversion = async (paymentType = null, paymentNotes = "") => {
    try {
      setIsAdmitting(true);
      if (!selectedRow?.id) {
        showErrorToast(t("receptionAppointments.toasts.invalidSelection"));
        return;
      }
      const appointmentId = selectedRow.id;
      const dp = Number(admitDownpayment) || 0;

      // Outpatient flow
      const paymentData = dp > 0 ? {
        payment_type: paymentType || admitPaymentType,
        payment_note: paymentNotes || ""
      } : {};
      await outpatientAPI.convertFromAppointment(appointmentId, dp, paymentData);
      showSuccessToast(t("receptionAppointments.toasts.convertSuccess"));

      // Remove appointment from local list
      const updated = localAppointments.filter((app) => app.id !== appointmentId);
      updateAppointments(updated);
      fetchAllTabData(true);
      setSelectedRow(null);
      setShowAdmitModal(false);
    } catch (error) {
      const message = handleApiError
        ? handleApiError(error)
        : error?.message || t("receptionAppointments.toasts.convertFailed");
      showErrorToast(message);
    } finally {
      setIsAdmitting(false);
    }
  };

  // Helper function to perform inpatient conversion
  const performInpatientConversion = async (paymentType = null, paymentNotes = "") => {
    try {
      setIsAdmitting(true);
      if (!selectedRow?.id) {
        showErrorToast(t("receptionAppointments.toasts.invalidSelection"));
        return;
      }
      const appointmentId = selectedRow.id;
      const dp = Number(admitDownpayment) || 0;

      // Inpatient flow
      const paymentData = dp > 0 ? {
        payment_type: paymentType || admitPaymentType,
        payment_note: paymentNotes || ""
      } : {};
      await inpatientAPI.convertFromAppointment(appointmentId, dp, paymentData);
      showSuccessToast(t("receptionAppointments.toasts.convertSuccess"));

      // Remove appointment from local list
      const updated = localAppointments.filter((app) => app.id !== appointmentId);
      updateAppointments(updated);
      fetchAllTabData(true);
      setSelectedRow(null);
      setShowAdmitInpatientModal(false);
    } catch (error) {
      const message = handleApiError
        ? handleApiError(error)
        : error?.message || t("receptionAppointments.toasts.convertFailed");
      showErrorToast(message);
    } finally {
      setIsAdmitting(false);
    }
  };

  return (
    <>
      <style>
        {`
          @keyframes fadeIn {
            from {
              opacity: 0;
            }
            to {
              opacity: 1;
            }
          }
          
          @keyframes scaleIn {
            from {
              opacity: 0;
              transform: scale(0.9) translateY(20px);
            }
            to {
              opacity: 1;
              transform: scale(1) translateY(0);
            }
          }
          
          @keyframes fadeInUp {
            from {
              opacity: 0;
              transform: translateY(10px);
            }
            to {
              opacity: 1;
              transform: translateY(0);
            }
          }
          
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          
          @keyframes pulse {
            0%, 100% {
              opacity: 1;
            }
            50% {
              opacity: 0.7;
            }
          }
        `}
      </style>
      <div
        className="reception-tab-content"
        dir={i18n.dir()}
        lang={i18n.language}
      >
        {/* Search Section */}
        <div className="reception-search-box">
          <div className="search-input-group" style={{ minWidth: '260px' }}>
            <label className="search-label">
              {t("receptionAppointments.search.date")}
            </label>
            <input
              ref={searchDateInputRef}
              type="text"
              className="reception-input"
              placeholder={t("receptionAppointments.search.datePlaceholder")}
              value={search.date}
              readOnly
            />
            <div style={{ display: 'flex', gap: '8px', marginTop: '8px' }}>
              <button
                type="button"
                className="quick-filter-btn"
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: search.today ? '#00d4b3' : '#f9fafb',
                  color: search.today ? '#fff' : '#6b7280',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  flex: 1,
                  transition: 'all 0.2s ease',
                  boxShadow: search.today ? '0 2px 4px rgba(0, 212, 179, 0.2)' : 'none'
                }}
                onClick={() => handleQuickFilter('today')}
              >
                {t("receptionAppointments.search.quick.today")}
              </button>
              <button
                type="button"
                className="quick-filter-btn"
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: search.tomorrow ? '#00d4b3' : '#f9fafb',
                  color: search.tomorrow ? '#fff' : '#6b7280',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  flex: 1,
                  transition: 'all 0.2s ease',
                  boxShadow: search.tomorrow ? '0 2px 4px rgba(0, 212, 179, 0.2)' : 'none'
                }}
                onClick={() => handleQuickFilter('tomorrow')}
              >
                {t("receptionAppointments.search.quick.tomorrow")}
              </button>
              <button
                type="button"
                className="quick-filter-btn"
                style={{
                  padding: '10px 16px',
                  borderRadius: '8px',
                  border: '1px solid #e5e7eb',
                  backgroundColor: search.upcoming ? '#00d4b3' : '#f9fafb',
                  color: search.upcoming ? '#fff' : '#6b7280',
                  fontSize: '0.85rem',
                  fontWeight: 700,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '2px',
                  flex: 1.5,
                  transition: 'all 0.2s ease',
                  boxShadow: search.upcoming ? '0 2px 4px rgba(0, 212, 179, 0.2)' : 'none',
                  whiteSpace: 'nowrap'
                }}
                onClick={() => handleQuickFilter('upcoming')}
              >
                {t("receptionAppointments.search.quick.upcoming")}
              </button>
            </div>
          </div>
          <div className="search-input-group">
            <label className="search-label">
              {t("receptionAppointments.search.petGuardian")}
            </label>
            <input
              type="text"
              placeholder={t("receptionAppointments.search.petGuardianPlaceholder")}
              className="reception-input"
              value={search.pet_owner}
              onChange={(e) => {
                setSearch((s) => ({ ...s, pet_owner: e.target.value }));
                setPage(1);
              }}
            />
          </div>
          <div className="search-input-group">
            <label className="search-label">
              {t("receptionAppointments.search.petName")}
            </label>
            <input
              type="text"
              placeholder={t("receptionAppointments.search.petNamePlaceholder")}
              className="reception-input"
              value={search.pet}
              onChange={(e) => {
                setSearch((s) => ({ ...s, pet: e.target.value }));
                setPage(1);
              }}
            />
          </div>
          <div className="search-input-group">
            <label className="search-label">
              {t("receptionAppointments.search.vetName")}
            </label>
            <input
              type="text"
              placeholder={t("receptionAppointments.search.vetNamePlaceholder")}
              className="reception-input"
              value={search.vet}
              onChange={(e) => {
                setSearch((s) => ({ ...s, vet: e.target.value }));
                setPage(1);
              }}
            />
          </div>
          <div className="search-input-group">
            <label className="search-label">
              {t("receptionAppointments.search.status")}
            </label>
            <select
              className="reception-input"
              value={search.status || ""}
              onChange={(e) => {
                const value = e.target.value;
                setSearch((s) => ({ ...s, status: value }));
                setPage(1);
              }}
            >
              <option value="">
                {t("receptionAppointments.search.statusOptions.all")}
              </option>
              <option value="pending">
                {formatStatus("pending")}
              </option>
              <option value="confirmed">
                {formatStatus("confirmed")}
              </option>
              <option value="completed">
                {formatStatus("completed")}
              </option>
              <option value="canceled">
                {formatStatus("canceled")}
              </option>
              <option value="checkout">
                {formatStatus("checkout")}
              </option>
              <option value="closed">
                {formatStatus("closed")}
              </option>
              <option value="no_show">
                {formatStatus("no_show")}
              </option>
              <option value="upcoming">
                {t("receptionAppointments.search.quick.upcoming")}
              </option>
            </select>
          </div>
          <div className="search-input-group">
            <label className="search-label">&nbsp;</label>
            <input
              type="button"
              className="reception-search-btn reception-input"
              value={
                isSearching
                  ? t("receptionAppointments.search.searching")
                  : t("receptionAppointments.search.button")
              }
              disabled={isSearching}
              onClick={async (e) => {
                e.preventDefault();
                setPage(1);

                // Map basic search fields to API parameters
                const searchParams = {
                  ...search,
                  // Override with specific datetime parameters for date search
                  date_from: search.date ? `${search.date}` : "",
                  date_to: search.date ? `${search.date}` : "",
                  user_name: search.pet_owner || search.user_name,
                  vet_name: search.vet || search.vet_name,
                  pet_name: search.pet || search.pet_name,
                  status: search.status || undefined,
                  search:
                    search.pet_owner ||
                    search.pet ||
                    search.vet ||
                    search.date ||
                    search.search,
                };

                await searchAppointments(searchParams);
              }}
            />
          </div>
        </div>

        {/* Cards Section */}
        <div className="reception-cards-wrapper">
          <div className="cards-header">
            <div className="cards-title" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <div className="cards-title-left">
                {t("receptionAppointments.header.title")}
                <span className="cards-count">
                  ({countLabel})
                  {searchResults.length > 0 && (
                    <span style={{ color: '#059669', marginLeft: '8px' }}>
                      🔍 {fallbackLabels.searchResults}
                    </span>
                  )}
                </span>
              </div>
              <div className="cards-title-right">
                {searchResults.length > 0 && (
                  <button
                    className="reception-action-btn"
                    style={{ marginRight: '8px', backgroundColor: '#6b7280' }}
                    onClick={() => {
                      // Clear search results
                      setSearchResults([]);
                      setTotalSearchResults(0);
                      setSearchPagination(null);
                      setPage(1);

                      // Reset all search filters
                      setSearch({
                        // Basic search fields
                        date: new Date().toISOString().split('T')[0], // Reset to current date
                        pet_owner: "",
                        pet: "",
                        vet: "",

                        // Advanced search parameters
                        user_id: "",
                        vet_id: "",
                        clinic_id: "",
                        pet_id: "",
                        pet_owner_id: "",
                        appointment_type_id: "",
                        status: "",
                        date_from: "",
                        date_to: "",
                        datetime_from: "",
                        datetime_to: "",
                        time_from: "",
                        time_to: "",
                        search: "",
                        user_name: "",
                        vet_name: "",
                        pet_name: "",
                        clinic_name: "",
                        canceled_by: "",
                        restored_by: "",
                        has_canceled_reason: false,
                        today: false,
                        tomorrow: false,
                        this_week: false,
                        this_month: false,
                        upcoming: false,
                        past: false,
                        sort_by: "appointment_at",
                        sort_order: "desc",
                        per_page: 5
                      });

                      // Reset date picker to today's date if it exists
                      if (searchDatePickerRef.current) {
                        searchDatePickerRef.current.setDate(new Date(), true);
                      }
                    }}
                  >
                    {t("receptionAppointments.search.clear")}
                  </button>
                )}
                <div className="view-toggle" style={{ display: 'flex', gap: '8px', marginRight: '12px' }}>
                  <button
                    className={`reception-action-btn ${viewType === 'table' ? 'active' : ''}`}
                    style={{
                      backgroundColor: viewType === 'table' ? '#00d4b3' : '#f3f4f6',
                      color: viewType === 'table' ? '#fff' : '#4b5563',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    onClick={() => {
                      setViewType('table');
                      // Reset agenda mode when going back to list for a focused view
                      if (search.upcoming) {
                        handleQuickFilter('today');
                      }
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <line x1="3" y1="6" x2="21" y2="6" />
                      <line x1="3" y1="12" x2="21" y2="12" />
                      <line x1="3" y1="18" x2="21" y2="18" />
                    </svg>
                    {t("receptionAppointments.views.table")}
                  </button>
                  <button
                    className={`reception-action-btn ${viewType === 'calendar' ? 'active' : ''}`}
                    style={{
                      backgroundColor: viewType === 'calendar' ? '#00d4b3' : '#f3f4f6',
                      color: viewType === 'calendar' ? '#fff' : '#4b5563',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    onClick={() => {
                      setViewType('calendar');
                      // If switch to calendar and still on default 'today', auto-trigger 'today' search
                      // to ensure we have enough data (20 items) for the calendar view
                      if (search.date === new Date().toISOString().split('T')[0] && !search.upcoming && !search.today) {
                        handleQuickFilter('today', 'calendar');
                      }
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    {t("receptionAppointments.views.calendar")}
                  </button>
                </div>
                <button
                  className="reception-action-btn"
                  onClick={() => setShowNewModal(true)}
                  disabled={isSaving || isUpdating || isAdmitting}
                >
                  {isSaving ? (
                    <>
                      <div className="button-spinner"></div>
                      {t("receptionAppointments.buttons.saving")}
                    </>
                  ) : (
                    t("receptionAppointments.buttons.new")
                  )}
                </button>
              </div>
            </div>
          </div>



          {/* Define visible rows with filtering applied if no specific status is selected */}
          {(() => {
            const visibleRows = paginatedRows;

            return (
              <>
                {loadingLists || isSearching ? (
                  <div className="cards-loading-state">
                    <div className="loading-spinner">
                      <div className="spinner"></div>
                    </div>
                  </div>
                ) : visibleRows.length === 0 ? (
                  <div className="cards-empty-state" style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '60px 20px',
                    color: '#6b7280',
                    textAlign: 'center',
                    backgroundColor: '#fff',
                    borderRadius: '12px',
                    border: '1px solid #e5e7eb',
                    margin: '20px 0'
                  }}>
                    <div className="empty-content">
                      <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" style={{ color: '#d1d5db', marginBottom: '16px' }}>
                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                        <line x1="16" y1="2" x2="16" y2="6" />
                        <line x1="8" y1="2" x2="8" y2="6" />
                        <line x1="3" y1="10" x2="21" y2="10" />
                        <line x1="8" y1="14" x2="8.01" y2="14" />
                        <line x1="12" y1="14" x2="12.01" y2="14" />
                        <line x1="16" y1="14" x2="16.01" y2="14" />
                        <line x1="8" y1="18" x2="8.01" y2="18" />
                        <line x1="12" y1="18" x2="12.01" y2="18" />
                      </svg>
                      <h4 style={{ fontSize: '1.1rem', fontWeight: 600, color: '#374151', marginBottom: '8px' }}>
                        {t("receptionAppointments.empty.title")}
                      </h4>
                      <p style={{ maxWidth: '400px', margin: '0 auto', fontSize: '0.95rem' }}>
                        {t("receptionAppointments.empty.description")}
                      </p>
                    </div>
                  </div>
                ) : (
                  <>
                    {viewType === 'table' ? (
                      <div className="appointments-table-container">
                        <table className="reception-table" style={{ width: '100%', borderCollapse: 'separate', borderSpacing: 0 }}>
                          <thead>
                            <tr style={{ backgroundColor: '#f9fafb', color: '#374151' }}>
                              <th style={{ textAlign: isRTL ? 'right' : 'left', padding: '12px 16px', fontWeight: 600 }}>
                                {t("receptionAppointments.table.date")}
                              </th>
                              <th style={{ textAlign: isRTL ? 'right' : 'left', padding: '12px 16px', fontWeight: 600 }}>
                                {t("receptionAppointments.table.time")}
                              </th>
                              <th style={{ textAlign: isRTL ? 'right' : 'left', padding: '12px 16px', fontWeight: 600 }}>
                                {t("receptionAppointments.table.pet")}
                              </th>
                              <th style={{ textAlign: isRTL ? 'right' : 'left', padding: '12px 16px', fontWeight: 600 }}>
                                {t("receptionAppointments.table.owner")}
                              </th>
                              <th style={{ textAlign: isRTL ? 'right' : 'left', padding: '12px 16px', fontWeight: 600 }}>
                                {t("receptionAppointments.table.vet")}
                              </th>
                              <th style={{ textAlign: isRTL ? 'right' : 'left', padding: '12px 16px', fontWeight: 600 }}>
                                {t("receptionAppointments.table.reason")}
                              </th>
                              <th style={{ textAlign: isRTL ? 'right' : 'left', padding: '12px 16px', fontWeight: 600 }}>
                                {t("receptionAppointments.table.status")}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {visibleRows.map((row, idx) => {
                              const isSelected = selectedRow?.id === row.id && selectedRow?.date === row.date && selectedRow?.time === row.time;
                              const petName = row.pet?.name || row.pet || fallbackLabels.pet;
                              const ownerName = row.pet_owner || fallbackLabels.owner;
                              const vetName = row.vet || fallbackLabels.vet;
                              const dateValue = row.date
                                ? new Date(`${row.date}T00:00:00`).toLocaleDateString(locale)
                                : fallbackLabels.date;
                              // Normalize time string for robust parsing (handle space vs T)
                              const normalizedTime = row.time ? row.time.replace(' ', 'T') : '';
                              const parseLocalTime = (str) => {
                                if (!str) return null;
                                if (!str.includes('T')) return new Date(`1970-01-01T${str}:00`);
                                // Treat as local time by stripping 'Z' if it exists to avoid timezone offset shifts
                                const localStr = str.endsWith('Z') ? str.slice(0, -1) : str;
                                return new Date(localStr);
                              };
                              const timeValue = normalizedTime
                                ? parseLocalTime(normalizedTime).toLocaleTimeString(locale, {
                                  hour: '2-digit',
                                  minute: '2-digit',
                                })
                                : fallbackLabels.time;
                              const reasonText = row.reason || formatAppointmentReason(dateValue, timeValue, vetName);
                              const statusText = formatStatus(row.status || "pending");
                              return (
                                <tr
                                  key={idx + (page - 1) * ROWS_PER_PAGE}
                                  data-appointment-id={row.id}
                                  onContextMenu={(e) => handleRowRightClick(row, e)}
                                  onClick={(e) => handleRowClick(row, e)}
                                  onMouseEnter={(e) => handleRowHover(row, e)}
                                  onMouseLeave={handleRowLeave}
                                  className={`reception-table-row ${isSelected ? 'row-selected' : ''}`}
                                  style={{
                                    cursor: 'context-menu',
                                    backgroundColor: isSelected ? '#f0f9ff' : 'transparent',
                                    transition: 'background-color 0.15s ease',
                                  }}
                                >
                                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>{dateValue}</td>
                                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>{timeValue}</td>
                                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>{petName}</td>
                                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>{ownerName}</td>
                                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb' }}>{vetName}</td>
                                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', maxWidth: 360, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                    {reasonText}
                                  </td>
                                  <td style={{ padding: '12px 16px', borderBottom: '1px solid #e5e7eb', textTransform: 'capitalize' }}>
                                    {statusText}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="appointments-calendar-view" style={{
                        display: 'flex',
                        gap: '24px',
                        overflowX: 'auto',
                        padding: '10px 4px 24px 4px',
                        alignItems: 'flex-start',
                        minHeight: '600px' // Ensure enough height for the columns
                      }}>
                        {Object.entries(
                          visibleRows.reduce((acc, row) => {
                            const date = row.date || 'unknown';
                            if (!acc[date]) acc[date] = [];
                            acc[date].push(row);
                            return acc;
                          }, {})
                        ).sort(([dateA], [dateB]) => new Date(dateA) - new Date(dateB)).map(([date, dayAppointments]) => {
                          const dateValue = date !== 'unknown'
                            ? new Date(`${date}T00:00:00`).toLocaleDateString(locale, { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
                            : fallbackLabels.date;

                          return (
                            <div key={date} className="calendar-day-group" style={{
                              minWidth: '340px',
                              flex: '0 0 auto',
                              backgroundColor: '#f8fafc', // Subtle background for the column
                              borderRadius: '16px',
                              padding: '16px',
                              border: '1px solid #e2e8f0',
                              display: 'flex',
                              flexDirection: 'column',
                              maxHeight: 'calc(100vh - 250px)', // Constrain height to viewport
                            }}>
                              <div className="day-header" style={{
                                padding: '0 0 16px 0',
                                marginBottom: '0',
                                fontWeight: 700,
                                color: '#1e293b',
                                fontSize: '1.1rem',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '10px',
                                borderBottom: '1px solid #e2e8f0',
                                position: 'sticky',
                                top: 0,
                                backgroundColor: '#f8fafc',
                                zIndex: 10
                              }}>
                                <div style={{
                                  width: '32px',
                                  height: '32px',
                                  borderRadius: '8px',
                                  backgroundColor: '#fff',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                                  color: '#00d4b3'
                                }}>
                                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                                    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                    <line x1="16" y1="2" x2="16" y2="6" />
                                    <line x1="8" y1="2" x2="8" y2="6" />
                                    <line x1="3" y1="10" x2="21" y2="10" />
                                  </svg>
                                </div>
                                {dateValue}
                                <span style={{
                                  marginLeft: 'auto',
                                  fontSize: '0.75rem',
                                  backgroundColor: '#fff',
                                  padding: '2px 8px',
                                  borderRadius: '10px',
                                  color: '#64748b',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
                                }}>
                                  {dayAppointments.length}
                                </span>
                              </div>
                              <div className="calendar-cards-grid" style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '12px',
                                overflowY: 'auto', // Allow scrolling within the column if many cards
                                paddingTop: '16px',
                                paddingRight: '4px' // Space for scrollbar
                              }}>
                                {dayAppointments.sort((a, b) => (a.time || '').localeCompare(b.time || '')).map((row, idx) => {
                                  const isSelected = selectedRow?.id === row.id && selectedRow?.date === row.date && selectedRow?.time === row.time;
                                  const petName = row.pet?.name || row.pet || fallbackLabels.pet;
                                  const statusText = formatStatus(row.status || "pending");

                                  // Time parsing for card
                                  const normalizedTime = row.time ? row.time.replace(' ', 'T') : '';
                                  const parseLocalTime = (str) => {
                                    if (!str) return null;
                                    if (!str.includes('T')) return new Date(`1970-01-01T${str}:00`);
                                    // Treat as local time by stripping 'Z' if it exists to avoid timezone offset shifts
                                    const localStr = str.endsWith('Z') ? str.slice(0, -1) : str;
                                    return new Date(localStr);
                                  };
                                  const timeValue = normalizedTime
                                    ? parseLocalTime(normalizedTime).toLocaleTimeString(locale, {
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })
                                    : fallbackLabels.time;

                                  return (
                                    <div
                                      key={`${row.id}-${idx}`}
                                      className={`appointment-mini-card ${isSelected ? 'selected' : ''}`}
                                      onClick={(e) => handleRowClick(row, e)}
                                      onContextMenu={(e) => handleRowRightClick(row, e)}
                                      style={{
                                        backgroundColor: isSelected ? '#f0f9ff' : '#fff',
                                        border: `2px solid ${isSelected ? '#00d4b3' : '#e5e7eb'}`,
                                        borderRadius: '12px',
                                        padding: '12px',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s ease',
                                        boxShadow: isSelected ? '0 4px 12px rgba(0, 212, 179, 0.15)' : 'none',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        gap: '8px'
                                      }}
                                      onMouseEnter={(e) => {
                                        if (!isSelected) e.currentTarget.style.borderColor = '#00d4b3';
                                      }}
                                      onMouseLeave={(e) => {
                                        if (!isSelected) e.currentTarget.style.borderColor = '#e5e7eb';
                                      }}
                                    >
                                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <span style={{ fontSize: '1.25rem', fontWeight: 800, color: '#111827' }}>{petName}</span>
                                        <span style={{
                                          fontSize: '0.75rem',
                                          fontWeight: 700,
                                          padding: '2px 8px',
                                          borderRadius: '12px',
                                          backgroundColor: row.status === 'urgent' ? '#fee2e2' : '#f3f4f6',
                                          color: row.status === 'urgent' ? '#ef4444' : '#6b7280'
                                        }}>
                                          {statusText}
                                        </span>
                                      </div>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', color: '#00d4b3', fontWeight: 700 }}>
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                          <circle cx="12" cy="12" r="10" />
                                          <path d="M12 6v6l4 2" />
                                        </svg>
                                        {timeValue}
                                      </div>
                                      <div style={{ fontSize: '0.875rem', color: '#6b7280', marginTop: '4px' }}>
                                        <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                            <circle cx="12" cy="7" r="4" />
                                          </svg>
                                          {row.pet_owner || fallbackLabels.owner}
                                        </div>
                                        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '4px', marginTop: '4px' }}>
                                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ marginTop: '2px', flexShrink: 0 }}>
                                            <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" />
                                          </svg>
                                          <span style={{
                                            fontSize: '0.8rem',
                                            lineHeight: '1.2',
                                            display: '-webkit-box',
                                            WebkitLineClamp: '2',
                                            WebkitBoxOrient: 'vertical',
                                            overflow: 'hidden',
                                            color: '#6b7280'
                                          }}>
                                            {row.reason || formatAppointmentReason(
                                              row.date ? new Date(`${row.date}T00:00:00`).toLocaleDateString(locale) : fallbackLabels.date,
                                              timeValue,
                                              row.vet || fallbackLabels.vet
                                            )}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}

                    {(totalPages > 1 || true) && (
                      <div className="reception-pagination" style={{
                        direction: isRTL ? 'rtl' : 'ltr',
                        justifyContent: isRTL ? 'flex-start' : 'center',
                        textAlign: isRTL ? 'right' : 'center'
                      }}>
                        <button
                          className="reception-action-btn"
                          onClick={async () => {
                            const newPage = Math.max(1, page - 1);
                            setPage(newPage);

                            if (searchResults.length > 0) {
                              const searchParams = {
                                ...search,
                                datetime_from: search.date ? `${search.date} 00:00:00` : '',
                                datetime_to: search.date ? `${search.date} 23:59:59` : '',
                                user_name: search.pet_owner || search.user_name,
                                vet_name: search.vet || search.vet_name,
                                pet_name: search.pet || search.pet_name,
                                status: search.status || undefined,
                                search: search.pet_owner || search.pet || search.vet || search.date || search.search,
                                page: newPage
                              };
                              await searchAppointments(searchParams);
                            }
                          }}
                          disabled={page === 1 || isSearching}
                          style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                        >
                          {isRTL ? "→ " : "← "}
                          {t("receptionAppointments.pagination.previous")}
                        </button>
                        <span style={{ margin: "0 12px", fontWeight: 600 }}>
                          {t("receptionAppointments.pagination.pageCount", {
                            current: page,
                            total: totalPages,
                          })}
                        </span>
                        <button
                          className="reception-action-btn"
                          onClick={async () => {
                            const newPage = Math.min(totalPages, page + 1);
                            setPage(newPage);

                            if (searchResults.length > 0) {
                              const searchParams = {
                                ...search,
                                datetime_from: search.date ? `${search.date} 00:00:00` : '',
                                datetime_to: search.date ? `${search.date} 23:59:59` : '',
                                user_name: search.pet_owner || search.user_name,
                                vet_name: search.vet || search.vet_name,
                                pet_name: search.pet || search.pet_name,
                                status: search.status || undefined,
                                search: search.pet_owner || search.pet || search.vet || search.date || search.search,
                                page: newPage
                              };
                              await searchAppointments(searchParams);
                            }
                          }}
                          disabled={page === totalPages || isSearching}
                          style={{ direction: isRTL ? 'rtl' : 'ltr' }}
                        >
                          {t("receptionAppointments.pagination.next")}{" "}
                          {isRTL ? "←" : "→"}
                        </button>
                      </div>
                    )}
                  </>
                )}
              </>
            );
          })()}
        </div>

        {/* Tooltip Menu */}
        {
          showTooltip && tooltipRow && (
            <div
              className="tooltip-menu"
              style={{
                position: 'fixed',
                top: tooltipPosition.y + 10,
                left: tooltipPosition.x + 10,
                zIndex: 1000,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.3)',
                borderRadius: '16px',
                boxShadow: '0 20px 40px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1), inset 0 1px 0 rgba(255, 255, 255, 0.6)',
                padding: '8px 0',
                width: 'fit-content',
                minWidth: '200px',
                maxWidth: '240px',
                animation: 'fadeInUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                overflow: 'hidden'
              }}
            >
              <button
                className="tooltip-menu-item"
                onClick={() => handleTooltipAction('edit', tooltipRow)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#374151',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  borderRadius: '0',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.08)';
                  e.target.style.color = '#1e40af';
                  e.target.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#374151';
                  e.target.style.transform = 'translateX(0)';
                }}
              >
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                  display: 'grid',
                  placeItems: 'center',
                  boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)',
                  flexShrink: 0
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                    <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', lineHeight: 1.2 }}>
                    {t("receptionAppointments.tooltip.edit.title")}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                    {t("receptionAppointments.tooltip.edit.subtitle")}
                  </div>
                </div>
              </button>

              <div style={{
                height: '1px',
                background: 'linear-gradient(90deg, transparent 0%, #e2e8f0 50%, transparent 100%)',
                margin: '4px 16px'
              }} />

              <button
                className="tooltip-menu-item"
                onClick={() => handleTooltipAction('cancel', tooltipRow)}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: 'pointer',
                  fontSize: '14px',
                  color: '#dc2626',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  borderRadius: '0',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.target.style.backgroundColor = 'rgba(239, 68, 68, 0.08)';
                  e.target.style.color = '#b91c1c';
                  e.target.style.transform = 'translateX(4px)';
                }}
                onMouseLeave={(e) => {
                  e.target.style.backgroundColor = 'transparent';
                  e.target.style.color = '#dc2626';
                  e.target.style.transform = 'translateX(0)';
                }}
              >
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                  display: 'grid',
                  placeItems: 'center',
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.3)',
                  flexShrink: 0
                }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                    <circle cx="12" cy="12" r="10" />
                    <line x1="15" y1="9" x2="9" y2="15" />
                    <line x1="9" y1="9" x2="15" y2="15" />
                  </svg>
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', lineHeight: 1.2 }}>
                    {t("receptionAppointments.tooltip.cancel.title")}
                  </div>
                  <div style={{ fontSize: '12px', color: '#64748b', marginTop: '2px' }}>
                    {t("receptionAppointments.tooltip.cancel.subtitle")}
                  </div>
                </div>
              </button>

              <div style={{
                height: '1px',
                background: 'linear-gradient(90deg, transparent 0%, #e2e8f0 50%, transparent 100%)',
                margin: '4px 16px'
              }} />

              <button
                className="tooltip-menu-item"
                onClick={() => {
                  if (!isFreePlan) {
                    handleTooltipAction('admit_outpatient', tooltipRow);
                  }
                }}
                disabled={isFreePlan}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: isFreePlan ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  color: isFreePlan ? '#9ca3af' : '#059669',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  borderRadius: '0',
                  position: 'relative',
                  opacity: isFreePlan ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isFreePlan) {
                    e.target.style.backgroundColor = 'rgba(5, 150, 105, 0.08)';
                    e.target.style.color = '#047857';
                    e.target.style.transform = 'translateX(4px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isFreePlan) {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#059669';
                    e.target.style.transform = 'translateX(0)';
                  }
                }}
                title={isFreePlan ? "Feature Locked, Upgrade to Unlock" : undefined}
              >
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  background: isFreePlan
                    ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                    : 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  display: 'grid',
                  placeItems: 'center',
                  boxShadow: isFreePlan
                    ? '0 4px 12px rgba(156, 163, 175, 0.3)'
                    : '0 4px 12px rgba(5, 150, 105, 0.3)',
                  flexShrink: 0,
                  opacity: isFreePlan ? 0.6 : 1
                }}>
                  {isFreePlan ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                    </svg>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', lineHeight: 1.2 }}>
                    {t("receptionAppointments.tooltip.admitOutpatient")}
                  </div>
                  <div style={{ fontSize: '12px', color: isFreePlan ? '#9ca3af' : '#64748b', marginTop: '2px' }}>
                    {t("receptionAppointments.tooltip.convert.subtitle")}
                  </div>
                </div>
              </button>

              <div style={{
                height: '1px',
                background: 'linear-gradient(90deg, transparent 0%, #e2e8f0 50%, transparent 100%)',
                margin: '4px 16px'
              }} />

              <button
                className="tooltip-menu-item"
                onClick={() => {
                  if (!isFreePlan) {
                    handleTooltipAction('admit_inpatient', tooltipRow);
                  }
                }}
                disabled={isFreePlan}
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  border: 'none',
                  background: 'transparent',
                  textAlign: 'left',
                  cursor: isFreePlan ? 'not-allowed' : 'pointer',
                  fontSize: '14px',
                  color: isFreePlan ? '#9ca3af' : '#1d4ed8',
                  fontWeight: '500',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  borderRadius: '0',
                  position: 'relative',
                  opacity: isFreePlan ? 0.6 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isFreePlan) {
                    e.target.style.backgroundColor = 'rgba(29, 78, 216, 0.08)';
                    e.target.style.color = '#1e40af';
                    e.target.style.transform = 'translateX(4px)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isFreePlan) {
                    e.target.style.backgroundColor = 'transparent';
                    e.target.style.color = '#1d4ed8';
                    e.target.style.transform = 'translateX(0)';
                  }
                }}
                title={isFreePlan ? "Feature Locked, Upgrade to Unlock" : undefined}
              >
                <div style={{
                  width: 32,
                  height: 32,
                  borderRadius: '8px',
                  background: isFreePlan
                    ? 'linear-gradient(135deg, #9ca3af 0%, #6b7280 100%)'
                    : 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                  display: 'grid',
                  placeItems: 'center',
                  boxShadow: isFreePlan
                    ? '0 4px 12px rgba(156, 163, 175, 0.3)'
                    : '0 4px 12px rgba(29, 78, 216, 0.3)',
                  flexShrink: 0,
                  opacity: isFreePlan ? 0.6 : 1
                }}>
                  {isFreePlan ? (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                  ) : (
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                      <path d="M3 3h18v18H3z" />
                      <path d="M9 9h6v6H9z" />
                    </svg>
                  )}
                </div>
                <div>
                  <div style={{ fontSize: '14px', fontWeight: '600', lineHeight: 1.2 }}>
                    {t("receptionAppointments.tooltip.admitInpatient", { defaultValue: 'Admit Inpatient' })}
                  </div>
                  <div style={{ fontSize: '12px', color: isFreePlan ? '#9ca3af' : '#64748b', marginTop: '2px' }}>
                    {t("receptionAppointments.tooltip.convertInpatient.subtitle", { defaultValue: 'Convert this appointment to a hospital admission' })}
                  </div>
                </div>
              </button>

            </div>
          )
        }

        {/* Help Tooltip */}
        {
          showHelpTooltip && (
            <div
              className="help-tooltip"
              style={{
                position: 'fixed',
                top: helpTooltipPosition.y - 50,
                left: helpTooltipPosition.x - 120,
                zIndex: 1001,
                backgroundColor: 'rgba(15, 23, 42, 0.95)',
                backdropFilter: 'blur(12px)',
                color: 'white',
                padding: '12px 16px',
                borderRadius: '12px',
                fontSize: '13px',
                fontWeight: '500',
                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.4), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                pointerEvents: 'none',
                animation: 'fadeInUp 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                maxWidth: '240px',
                textAlign: 'center',
                border: '1px solid rgba(255, 255, 255, 0.1)',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <div style={{
                width: 20,
                height: 20,
                borderRadius: '6px',
                background: 'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                display: 'grid',
                placeItems: 'center',
                flexShrink: 0
              }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <circle cx="12" cy="12" r="10" />
                  <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                  <line x1="12" y1="17" x2="12.01" y2="17" />
                </svg>
              </div>
              <div>
                <div style={{ fontSize: '13px', fontWeight: '600', lineHeight: 1.2 }}>
                  {t("receptionAppointments.tooltip.help.title")}
                </div>
                <div style={{ fontSize: '11px', color: '#cbd5e1', marginTop: '2px' }}>
                  {t("receptionAppointments.tooltip.help.subtitle")}
                </div>
              </div>
            </div>
          )
        }

        {/* Modals */}
        <AppointmentModal
          isOpen={showNewModal}
          onClose={() => setShowNewModal(false)}
          onSave={saveAppointmentToAPI}
          vets={vets}
          workingHours={clinicDataToUse?.clinic?.working_hours || []}
          appointmentTypes={clinicDataToUse?.appointment_types || []}
          isAppointments={true}
        />

        {/* Cancel Confirmation Modal */}
        {
          showCancelModal && appointmentToCancel && (
            <div
              className="reception-modal-overlay"
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '20px',
                animation: 'fadeIn 0.3s ease-out'
              }}
              onClick={() => closeCancelModal()}
            >
              <div
                className="reception-modal"
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '20px',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                  width: 'min(500px, 95vw)',
                  maxWidth: '500px',
                  position: 'relative',
                  overflow: 'hidden',
                  animation: 'scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
                onClick={(e) => e.stopPropagation()}
              >
                {/* Header */}
                <div
                  className="reception-modal-header"
                  style={{
                    padding: '24px 24px 20px',
                    background: 'linear-gradient(135deg, #fef2f2 0%, #fee2e2 100%)',
                    borderBottom: '1px solid #fecaca',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        display: 'grid',
                        placeItems: 'center',
                        boxShadow: '0 8px 24px rgba(239, 68, 68, 0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <line x1="15" y1="9" x2="9" y2="15" />
                          <line x1="9" y1="9" x2="15" y2="15" />
                        </svg>
                      </div>
                      <div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
                          {t("receptionAppointments.cancelModal.title")}
                        </div>
                        <div style={{ fontSize: 14, color: '#64748b', fontWeight: 500 }}>
                          {t("receptionAppointments.cancelModal.subtitle")}
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Content */}
                <div
                  className="reception-modal-form"
                  style={{
                    padding: '24px',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                  }}
                >
                  {/* Appointment Details Card */}
                  <div style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    borderRadius: '16px',
                    padding: '20px',
                    border: '1px solid #e2e8f0',
                    marginBottom: '24px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '16px' }}>
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        display: 'grid',
                        placeItems: 'center',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </div>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: '#1e293b' }}>
                          {t("receptionAppointments.cancelModal.details.title")}
                        </div>
                        <div style={{ fontSize: 13, color: '#64748b' }}>
                          {t("receptionAppointments.cancelModal.details.subtitle")}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: '4px' }}>
                          {t("receptionAppointments.table.pet")}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{appointmentToCancel.pet}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: '4px' }}>
                          {t("receptionAppointments.table.owner")}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{appointmentToCancel.pet_owner}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: '4px' }}>
                          {t("receptionAppointments.table.date")}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{formattedCancelDate}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: '4px' }}>
                          {t("receptionAppointments.table.time")}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{formattedCancelTime}</div>
                      </div>
                    </div>
                  </div>

                  {/* Warning Message */}
                  <div style={{
                    background: 'linear-gradient(135deg, #fef3c7 0%, #fde68a 100%)',
                    borderRadius: '12px',
                    padding: '16px',
                    border: '1px solid #f59e0b',
                    marginBottom: '24px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '12px'
                  }}>
                    <div style={{
                      width: 24,
                      height: 24,
                      borderRadius: '6px',
                      background: '#f59e0b',
                      display: 'grid',
                      placeItems: 'center',
                      flexShrink: 0,
                      marginTop: '2px'
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                        <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                        <line x1="12" y1="9" x2="12" y2="13" />
                        <line x1="12" y1="17" x2="12.01" y2="17" />
                      </svg>
                    </div>
                    <div>
                      <div style={{ fontSize: 14, fontWeight: 600, color: '#92400e', marginBottom: '4px' }}>
                        {t("receptionAppointments.cancelModal.warningTitle")}
                      </div>
                      <div style={{ fontSize: 13, color: '#92400e', lineHeight: 1.4 }}>
                        {t("receptionAppointments.cancelModal.warningBody")}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                    <button
                      className="reception-action-btn reception-modal-cancel"
                      onClick={() => closeCancelModal()}
                      style={{
                        padding: '12px 20px',
                        borderRadius: '12px',
                        border: '2px solid #e2e8f0',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#64748b',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)';
                        e.target.style.borderColor = '#cbd5e1';
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)';
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M9 12l2 2 4-4" />
                        <path d="M21 12c0 4.97-4.03 9-9 9s-9-4.03-9-9 4.03-9 9-9c2.12 0 4.08.74 5.64 1.98" />
                      </svg>
                      {t("receptionAppointments.cancelModal.buttons.keep")}
                    </button>
                    <button
                      className="reception-action-btn"
                      onClick={() => confirmCancellation()}
                      style={{
                        padding: '12px 20px',
                        borderRadius: '12px',
                        border: 'none',
                        background: 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: 'white',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 8px 24px rgba(239, 68, 68, 0.4), 0 0 0 1px rgba(239, 68, 68, 0.2)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 12px 32px rgba(239, 68, 68, 0.5), 0 0 0 1px rgba(239, 68, 68, 0.3)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 8px 24px rgba(239, 68, 68, 0.4), 0 0 0 1px rgba(239, 68, 68, 0.2)';
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <line x1="15" y1="9" x2="9" y2="15" />
                        <line x1="9" y1="9" x2="15" y2="15" />
                      </svg>
                      {t("receptionAppointments.cancelModal.buttons.confirm")}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )
        }

        <EditAppointmentModal
          isOpen={showEditModal}
          onClose={() => {
            setShowEditModal(false);
            setEditAppointmentData(null);
          }}
          onSave={updateAppointmentToAPI}
          vets={vets}
          workingHours={clinicDataToUse?.clinic?.working_hours || []}
          appointmentTypes={clinicDataToUse?.appointment_types || []}
          isAppointments={true}
          editAppointment={editAppointmentData}
        />

        {/* Admit Modal */}
        {
          showAdmitModal && selectedRow && (
            <div
              className="reception-modal-overlay"
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '20px',
                animation: 'fadeIn 0.3s ease-out'
              }}
            >
              <div
                className="reception-modal"
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '20px',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                  width: 'min(600px, 95vw)',
                  maxWidth: '600px',
                  position: 'relative',
                  overflow: 'hidden',
                  animation: 'scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                {/* Header */}
                <div
                  className="reception-modal-header"
                  style={{
                    padding: '24px 24px 20px',
                    background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
                    borderBottom: '1px solid #bbf7d0',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        display: 'grid',
                        placeItems: 'center',
                        boxShadow: '0 8px 24px rgba(5, 150, 105, 0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                          <path d="M22 12h-4l-3 9L9 3l-3 9H2" />
                        </svg>
                      </div>
                      <div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
                          {t("receptionAppointments.admitModal.title")}
                        </div>
                        <div style={{ fontSize: 14, color: '#64748b', fontWeight: 500 }}>
                          {t("receptionAppointments.admitModal.subtitle")}
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Content */}
                <form
                  className="reception-modal-form"
                  onSubmit={(e) => e.preventDefault()}
                  style={{
                    padding: '24px',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                  }}
                >
                  {/* Appointment Details Card */}
                  <div style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    borderRadius: '16px',
                    padding: '20px',
                    border: '1px solid #e2e8f0',
                    marginBottom: '24px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '16px' }}>
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        display: 'grid',
                        placeItems: 'center',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </div>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: '#1e293b' }}>
                          {t("receptionAppointments.admitModal.details.title")}
                        </div>
                        <div style={{ fontSize: 13, color: '#64748b' }}>
                          {t("receptionAppointments.admitModal.details.subtitle")}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: '4px' }}>
                          {t("receptionAppointments.admitModal.labels.petGuardian")}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{selectedRow.pet_owner}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: '4px' }}>
                          {t("receptionAppointments.table.pet")}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{selectedRow.pet}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: '4px' }}>
                          {t("receptionAppointments.table.vet")}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{selectedRow.vet}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: '4px' }}>
                          {t("receptionAppointments.admitModal.labels.dateTime")}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                          {formattedSelectedDate} {t("receptionAppointments.admitModal.labels.at")} {formattedSelectedTime}
                        </div>
                      </div>
                    </div>

                    {selectedRow.reason && (
                      <div style={{ marginTop: '16px' }}>
                        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: '4px' }}>
                          {t("receptionAppointments.table.reason")}
                        </div>
                        <div style={{
                          fontSize: 14,
                          color: '#1e293b',
                          background: '#f8fafc',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}>
                          {selectedRow.reason}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Downpayment Section */}
                  <div style={{ marginBottom: '24px' }}>
                    <label
                      className="reception-modal-label"
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1e293b',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <div style={{
                        width: 20,
                        height: 20,
                        borderRadius: '6px',
                        background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                        display: 'grid',
                        placeItems: 'center'
                      }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 6v6l4 2" />
                        </svg>
                      </div>
                      {t("receptionAppointments.admitModal.downpayment.label")}
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="number"
                        className="reception-modal-input"
                        value={admitDownpayment}
                        onChange={(e) => setAdmitDownpayment(Number(e.target.value))}
                        placeholder={t("receptionAppointments.admitModal.downpayment.placeholder")}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '12px',
                          border: '2px solid #e2e8f0',
                          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                          fontSize: '14px',
                          color: '#1e293b',
                          outline: 'none',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 3px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#059669';
                          e.target.style.boxShadow = '0 6px 16px rgba(5, 150, 105, 0.15), inset 0 1px 0 rgba(255,255,255,0.5)';
                          e.target.style.transform = 'translateY(-1px)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e2e8f0';
                          e.target.style.boxShadow = '0 3px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)';
                          e.target.style.transform = 'translateY(0)';
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '12px',
                        color: '#64748b',
                        fontWeight: '500'
                      }}>
                        {t("receptionAppointments.admitModal.downpayment.currency")}
                      </div>
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#64748b',
                      marginTop: '6px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M12 6v6l4 2" />
                      </svg>
                      {t("receptionAppointments.admitModal.downpayment.hint")}
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div
                    className="reception-modal-actions"
                    style={{
                      display: 'flex',
                      gap: '12px',
                      justifyContent: 'flex-end',
                      paddingTop: '16px',
                      borderTop: '1px solid #e2e8f0'
                    }}
                  >
                    <button
                      type="button"
                      className="reception-action-btn reception-modal-cancel"
                      onClick={() => {
                        setShowAdmitModal(false);
                        setAdmitDownpayment('');
                        // Reset to default payment type
                        const defaultType = paymentTypes.find(type =>
                          type.label.toLowerCase().includes('credit') ||
                          type.label.toLowerCase().includes('card')
                        ) || paymentTypes[0];
                        setAdmitPaymentType(defaultType?.value || "2"); // Default to Credit Card ID
                      }}
                      style={{
                        padding: '12px 20px',
                        borderRadius: '12px',
                        border: '2px solid #e2e8f0',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#64748b',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)';
                        e.target.style.borderColor = '#cbd5e1';
                        e.target.style.transform = 'translateY(-1px)';
                        e.target.style.boxShadow = '0 8px 20px rgba(0,0,0,0.1)';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)';
                        e.target.style.borderColor = '#e2e8f0';
                        e.target.style.transform = 'translateY(0)';
                        e.target.style.boxShadow = '0 4px 12px rgba(0,0,0,0.05)';
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                      {t("receptionAppointments.admitModal.buttons.cancel")}
                    </button>
                    <button
                      type="button"
                      className="reception-action-btn"
                      disabled={isAdmitting}
                      onClick={async () => {
                        const dp = Number(admitDownpayment) || 0;

                        // Commented out payment modal logic - will be used later
                        // if (dp > 0) {
                        //   // Show payment modal when downpayment > 0
                        //   setShowOutpatientPaymentModal(true);
                        // } else {
                        //   // Proceed directly when no downpayment
                        //   await performOutpatientConversion();
                        // }

                        // For now, always proceed directly
                        await performOutpatientConversion();
                      }}
                    >
                      {isAdmitting ? (
                        <>
                          <span style={{
                            width: 16,
                            height: 16,
                            border: '2px solid #FFFFFF',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            display: 'inline-block',
                            animation: 'spin 1s linear infinite'
                          }} />
                          {t("receptionAppointments.admitModal.buttons.processing")}
                        </>
                      ) : (
                        t("receptionAppointments.admitModal.buttons.confirm")
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )
        }

        {/* Admit Inpatient Modal */}
        {
          showAdmitInpatientModal && selectedRow && (
            <div
              className="reception-modal-overlay"
              style={{
                position: 'fixed',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0, 0, 0, 0.6)',
                backdropFilter: 'blur(8px)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                zIndex: 1000,
                padding: '20px',
                animation: 'fadeIn 0.3s ease-out'
              }}
            >
              <div
                className="reception-modal"
                style={{
                  backgroundColor: '#ffffff',
                  borderRadius: '20px',
                  boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25), 0 0 0 1px rgba(255, 255, 255, 0.1)',
                  width: 'min(600px, 95vw)',
                  maxWidth: '600px',
                  position: 'relative',
                  overflow: 'hidden',
                  animation: 'scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                  border: '1px solid rgba(255, 255, 255, 0.2)'
                }}
              >
                {/* Header */}
                <div
                  className="reception-modal-header"
                  style={{
                    padding: '24px 24px 20px',
                    background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
                    borderBottom: '1px solid #bfdbfe',
                    position: 'relative',
                    overflow: 'hidden'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                      <div style={{
                        width: 48,
                        height: 48,
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                        display: 'grid',
                        placeItems: 'center',
                        boxShadow: '0 8px 24px rgba(29, 78, 216, 0.3), inset 0 1px 0 rgba(255,255,255,0.2)'
                      }}>
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                          <path d="M3 3h18v18H3z" />
                          <path d="M9 9h6v6H9z" />
                        </svg>
                      </div>
                      <div>
                        <div style={{ fontSize: 20, fontWeight: 700, color: '#1e293b', lineHeight: 1.2 }}>
                          {t("receptionAppointments.admitInpatientModal.title", { defaultValue: 'Admit Inpatient' })}
                        </div>
                        <div style={{ fontSize: 14, color: '#64748b', fontWeight: 500 }}>
                          {t("receptionAppointments.admitInpatientModal.subtitle", { defaultValue: 'Admit this patient for hospital stay' })}
                        </div>
                      </div>
                    </div>

                  </div>
                </div>

                {/* Content */}
                <form
                  className="reception-modal-form"
                  onSubmit={(e) => e.preventDefault()}
                  style={{
                    padding: '24px',
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)'
                  }}
                >
                  {/* Appointment Details Card */}
                  <div style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                    borderRadius: '16px',
                    padding: '20px',
                    border: '1px solid #e2e8f0',
                    marginBottom: '24px',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: '16px' }}>
                      <div style={{
                        width: 40,
                        height: 40,
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)',
                        display: 'grid',
                        placeItems: 'center',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.3)'
                      }}>
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                          <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                          <line x1="16" y1="2" x2="16" y2="6" />
                          <line x1="8" y1="2" x2="8" y2="6" />
                          <line x1="3" y1="10" x2="21" y2="10" />
                        </svg>
                      </div>
                      <div>
                        <div style={{ fontSize: 16, fontWeight: 600, color: '#1e293b' }}>
                          {t("receptionAppointments.admitModal.details.title")}
                        </div>
                        <div style={{ fontSize: 13, color: '#64748b' }}>
                          {t("receptionAppointments.admitModal.details.subtitle")}
                        </div>
                      </div>
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: '4px' }}>
                          {t("receptionAppointments.admitModal.labels.petGuardian")}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{selectedRow.pet_owner}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: '4px' }}>
                          {t("receptionAppointments.table.pet")}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{selectedRow.pet}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: '4px' }}>
                          {t("receptionAppointments.table.vet")}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>{selectedRow.vet}</div>
                      </div>
                      <div>
                        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: '4px' }}>
                          {t("receptionAppointments.admitModal.labels.dateTime")}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 600, color: '#1e293b' }}>
                          {formattedSelectedDate} {t("receptionAppointments.admitModal.labels.at")} {formattedSelectedTime}
                        </div>
                      </div>
                    </div>

                    {selectedRow.reason && (
                      <div style={{ marginTop: '16px' }}>
                        <div style={{ fontSize: 12, color: '#64748b', fontWeight: 500, marginBottom: '4px' }}>
                          {t("receptionAppointments.table.reason")}
                        </div>
                        <div style={{
                          fontSize: 14,
                          color: '#1e293b',
                          background: '#f8fafc',
                          padding: '8px 12px',
                          borderRadius: '8px',
                          border: '1px solid #e2e8f0'
                        }}>
                          {selectedRow.reason}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Downpayment Section */}
                  <div style={{ marginBottom: '24px' }}>
                    <label
                      className="reception-modal-label"
                      style={{
                        display: 'block',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#1e293b',
                        marginBottom: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                    >
                      <div style={{
                        width: 20,
                        height: 20,
                        borderRadius: '6px',
                        background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                        display: 'grid',
                        placeItems: 'center'
                      }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                          <circle cx="12" cy="12" r="10" />
                          <path d="M12 6v6l4 2" />
                        </svg>
                      </div>
                      {t("receptionAppointments.admitModal.downpayment.label")}
                    </label>
                    <div style={{ position: 'relative' }}>
                      <input
                        type="number"
                        className="reception-modal-input"
                        value={admitDownpayment}
                        onChange={(e) => setAdmitDownpayment(Number(e.target.value))}
                        placeholder={t("receptionAppointments.admitModal.downpayment.placeholder")}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          borderRadius: '12px',
                          border: '2px solid #e2e8f0',
                          background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                          fontSize: '14px',
                          color: '#1e293b',
                          outline: 'none',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 3px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)'
                        }}
                        onFocus={(e) => {
                          e.target.style.borderColor = '#1d4ed8';
                          e.target.style.boxShadow = '0 6px 16px rgba(29, 78, 216, 0.15), inset 0 1px 0 rgba(255,255,255,0.5)';
                          e.target.style.transform = 'translateY(-1px)';
                        }}
                        onBlur={(e) => {
                          e.target.style.borderColor = '#e2e8f0';
                          e.target.style.boxShadow = '0 3px 8px rgba(0,0,0,0.05), inset 0 1px 0 rgba(255,255,255,0.5)';
                          e.target.style.transform = 'translateY(0)';
                        }}
                      />
                      <div style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '12px',
                        color: '#64748b',
                        fontWeight: '500'
                      }}>
                        {t("receptionAppointments.admitModal.downpayment.currency")}
                      </div>
                    </div>
                  </div>

                  {/* Action Buttons */}
                  <div
                    className="reception-modal-actions"
                    style={{
                      display: 'flex',
                      gap: '12px',
                      justifyContent: 'flex-end',
                      paddingTop: '16px',
                      borderTop: '1px solid #e2e8f0'
                    }}
                  >
                    <button
                      type="button"
                      className="reception-action-btn reception-modal-cancel"
                      onClick={() => {
                        setShowAdmitInpatientModal(false);
                        setAdmitDownpayment('');
                      }}
                      style={{
                        padding: '12px 20px',
                        borderRadius: '12px',
                        border: '2px solid #e2e8f0',
                        background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#64748b',
                        transition: 'all 0.3s ease',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.05)'
                      }}
                    >
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <line x1="18" y1="6" x2="6" y2="18" />
                        <line x1="6" y1="6" x2="18" y2="18" />
                      </svg>
                      {t("receptionAppointments.admitModal.buttons.cancel")}
                    </button>
                    <button
                      type="button"
                      className="reception-action-btn"
                      disabled={isAdmitting}
                      onClick={async () => {
                        await performInpatientConversion();
                      }}
                      style={{
                        background: 'linear-gradient(135deg, #1d4ed8 0%, #1e40af 100%)',
                        boxShadow: '0 8px 24px rgba(29, 78, 216, 0.3)'
                      }}
                    >
                      {isAdmitting ? (
                        <>
                          <span style={{
                            width: 16,
                            height: 16,
                            border: '2px solid #FFFFFF',
                            borderTop: '2px solid transparent',
                            borderRadius: '50%',
                            display: 'inline-block',
                            animation: 'spin 1s linear infinite'
                          }} />
                          {t("receptionAppointments.admitModal.buttons.processing")}
                        </>
                      ) : (
                        t("receptionAppointments.admitModal.buttons.confirm")
                      )}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )
        }

        {/* Commented out SelectPaymentModal - will be used later */}
        {/* 
      <SelectPaymentModal
        isOpen={showOutpatientPaymentModal}
        onClose={() => {
          setShowOutpatientPaymentModal(false);
          setOutpatientPaymentNotes("");
        }}
        onSave={async (paymentInfo) => {
          // Handle payment save and proceed with check-in
          await performOutpatientConversion(paymentInfo.method, paymentInfo.notes);
          setShowOutpatientPaymentModal(false);
          setOutpatientPaymentNotes("");
        }}
        isLoading={isAdmitting}
        title="Select Payment Method"
        paymentNotes={outpatientPaymentNotes}
        paymentType={admitPaymentType}
      />
      */}
      </div >
    </>
  );
};

export default AppointmentsTab;
